/**
 * Pure functions of the `cryptogram` type: the progress blob and the submission (ADR 0060).
 * No component here, so `module.ts` stays light in the eager bundle.
 */
import { cipherLetters, normalizeLetter } from "./cipher";
import type {
  CryptogramDefinition,
  CryptogramProgress,
  CryptogramSubmission,
  Mapping,
} from "./types";

export const DEFAULT_RULES = {
  fr: "Chaque lettre du texte a été remplacée par une autre, toujours la même : deux lettres différentes du texte chiffré cachent deux lettres différentes. Les espaces et la ponctuation sont d'origine. Écrivez une lettre sous une lettre chiffrée : elle s'inscrit partout où celle-ci apparaît. Aucune lettre ne se chiffre par elle-même.",
  en: "Every letter of the text was replaced by another, always the same one: two different letters of the ciphertext hide two different letters. Spaces and punctuation are the original ones. Write a letter under a cipher letter: it fills in wherever that cipher letter appears. No letter stands for itself.",
};

export function emptyDefinition(): CryptogramDefinition {
  return {
    puzzleType: "cryptogram",
    typeVersion: 1,
    language: "fr",
    ciphertext: "",
    givens: [],
    rulesText: { ...DEFAULT_RULES },
  };
}

export function emptyProgress(_definition: CryptogramDefinition): CryptogramProgress {
  return { version: 1, mapping: {} };
}

/** The givens as a mapping, restricted to letters of the ciphertext. */
export function givenMapping(definition: CryptogramDefinition): Mapping {
  const used = new Set(cipherLetters(definition.ciphertext));
  const out: Mapping = {};
  for (const given of definition.givens ?? [])
    if (used.has(given.cipher)) out[given.cipher] = given.plain;
  return out;
}

/**
 * The player's key, cleaned: only cipher letters of the text, one plain letter each, and never
 * a given (it is locked on the board).
 */
function cleanMapping(definition: CryptogramDefinition, raw: unknown): Mapping {
  if (typeof raw !== "object" || raw === null || Array.isArray(raw)) return {};
  const used = new Set(cipherLetters(definition.ciphertext));
  const givens = givenMapping(definition);
  const out: Mapping = {};
  for (const [cipher, value] of Object.entries(raw as Record<string, unknown>)) {
    if (!used.has(cipher) || cipher in givens || typeof value !== "string") continue;
    const plain = normalizeLetter(value);
    if (plain) out[cipher] = plain;
  }
  return out;
}

/** Accepts any stored blob (older versions, null, garbage) and returns a valid progress. */
export function migrateProgress(
  definition: CryptogramDefinition,
  raw: unknown,
): CryptogramProgress {
  const mapping =
    typeof raw === "object" && raw !== null && "mapping" in raw
      ? cleanMapping(definition, (raw as { mapping: unknown }).mapping)
      : {};
  return { version: 1, mapping };
}

/** Writes (or with "" clears) the plain letter under a cipher letter. A given does not move. */
export function setLetter(
  definition: CryptogramDefinition,
  progress: CryptogramProgress,
  cipher: string,
  plain: string,
): CryptogramProgress {
  if (cipher in givenMapping(definition)) return progress;
  const letter = normalizeLetter(plain);
  const mapping = { ...progress.mapping };
  if (letter) mapping[cipher] = letter;
  else delete mapping[cipher];
  return { ...progress, mapping };
}

/** The key as the board shows it: the givens, then what the player wrote. */
export function boardMapping(
  definition: CryptogramDefinition,
  progress: CryptogramProgress,
): Mapping {
  return { ...cleanMapping(definition, progress.mapping), ...givenMapping(definition) };
}

/** The player's key; the server adds the givens itself. */
export function serializeSubmission(
  definition: CryptogramDefinition,
  progress: CryptogramProgress,
): CryptogramSubmission {
  const mapping = cleanMapping(definition, progress.mapping);
  return {
    mapping: Object.fromEntries(Object.entries(mapping).sort(([a], [b]) => a.localeCompare(b))),
  };
}
